import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppWindow, UserRound, UserX } from 'lucide-react';
import styled from 'styled-components';

import { tagToneColors } from 'components/constants/tagTones';
import { IStudentSlackWorkspace, useStudentSlackWorkspaces } from 'core/hooks';

import { COMMS_COUNTER_ICONS } from '../CommsCounters';

/** Значок Slack — общий для раздела связи, а не свой импорт. */
const SlackIcon = COMMS_COUNTER_ICONS.slack;

/** Внешние ссылки — в новой вкладке и без доступа к нашему окну. */
const EXTERNAL = { target: '_blank', rel: 'noopener noreferrer' } as const;

interface IStudentSlackLinksProps {
  workspace: IStudentSlackWorkspace;
  /**
   * `all` — всё, что можно сделать в Slack группы (карточка студента);
   * `deactivate` — только деактивация (окно блокировки LMS).
   */
  variant?: 'all' | 'deactivate';
  /** Назвать группу — когда строка стоит не под своей группой. */
  showGroup?: boolean;
  id?: string;
}

/**
 * #54 — Slack группы студента: ссылки и ручная деактивация.
 *
 * Ссылки собирает СЕРВЕР (`StudentSlackWorkspaceType`): адрес workspace,
 * профиль студента и админка участников. Прав бота ERP не трогает (решение
 * владельца 07.10.2026) — деактивирует человек в админке Slack, а здесь только
 * дорога туда и подсказка, что там нажать.
 *
 * * студент в этом Slack не найден (`slackUserId` пуст) — так и сказано, а
 *   ссылка одна: открыть Slack группы;
 * * адрес workspace ещё не известен — профиля и админки нет, это тоже сказано;
 * * `slack://` — только вторым вариантом «в приложении»: без установленного
 *   Slack такая ссылка молча ничего не делает.
 */
export const StudentSlackLinks = memo(
  ({ workspace, variant = 'all', showGroup, id }: IStudentSlackLinksProps) => {
    const { t } = useTranslation();
    const found = Boolean(workspace.slackUserId);
    const urlUnknown = found && !workspace.adminUrl && !workspace.profileUrl;
    const all = variant === 'all';

    return (
      <Box id={id} data-testid="student-slack-links">
        <Head>
          {showGroup && <GroupName>{workspace.groupName}</GroupName>}
          <Muted>
            {workspace.workspaceName}
            {' · '}
            {found
              ? t('pages.studentDetail.slack.asName', {
                  name: workspace.displayName || workspace.slackUserId,
                })
              : t('pages.studentDetail.slack.notFound')}
          </Muted>
        </Head>

        <Links>
          {(all || !found || urlUnknown) && (
            <ExternalLink href={workspace.openWorkspaceUrl} {...EXTERNAL}>
              <SlackIcon size={13} strokeWidth={2} aria-hidden="true" />
              {t('pages.studentDetail.slack.openWorkspace')}
            </ExternalLink>
          )}
          {all && found && workspace.profileUrl && (
            <ExternalLink href={workspace.profileUrl} {...EXTERNAL}>
              <UserRound size={13} strokeWidth={2} aria-hidden="true" />
              {t('pages.studentDetail.slack.profile')}
            </ExternalLink>
          )}
          {/* `slack://` — вторым вариантом рядом с веб-профилем («в
              приложении»). Веб-профиля нет (адрес workspace не известен) —
              тогда это единственный профиль, и подпись полная: одинокое «в
              приложении» читалось бы обрывком. */}
          {all && found && workspace.appProfileUrl && (
            <ExternalLink
              href={workspace.appProfileUrl}
              aria-label={
                workspace.profileUrl
                  ? t('pages.studentDetail.slack.profileAppLabel')
                  : undefined
              }
            >
              <AppWindow size={13} strokeWidth={2} aria-hidden="true" />
              {workspace.profileUrl
                ? t('pages.studentDetail.slack.profileApp')
                : t('pages.studentDetail.slack.profileAppLabel')}
            </ExternalLink>
          )}
          {found && workspace.adminUrl && (
            <ExternalLink
              href={workspace.adminUrl}
              {...EXTERNAL}
              data-tone="danger"
              aria-describedby={id ? `${id}-deactivate-hint` : undefined}
            >
              <UserX size={13} strokeWidth={2} aria-hidden="true" />
              {t('pages.studentDetail.slack.deactivate')}
            </ExternalLink>
          )}
        </Links>

        {found && workspace.adminUrl && (
          <Hint id={id ? `${id}-deactivate-hint` : undefined}>
            {t('pages.studentDetail.slack.deactivateHint')}
          </Hint>
        )}
        {urlUnknown && (
          <Hint>{t('pages.studentDetail.slack.noWorkspaceUrl')}</Hint>
        )}
      </Box>
    );
  },
);

StudentSlackLinks.displayName = 'StudentSlackLinks';

interface IStudentSlackDeactivateHintProps {
  studentId: string;
}

/**
 * Подсказка в окне блокировки LMS (#54): блокировка закрывает LMS, но не
 * Slack групп — там студента деактивируют вручную, и ссылки лежат здесь же.
 * Роли без доступа к Slack студента не видят ничего (и запроса нет).
 */
export const StudentSlackDeactivateHint = memo(
  ({ studentId }: IStudentSlackDeactivateHintProps) => {
    const { t } = useTranslation();
    const { canSee, workspaces, loading, error } = useStudentSlackWorkspaces(
      studentId,
    );
    if (!canSee) return null;
    // Пусто по ОТВЕТУ — Slack у групп студента не подключён, говорить не о
    // чем. Пока ответа нет — место держим; отказ — говорим, а не молчим:
    // пропавшая подсказка читалась бы как «в Slack ничего делать не надо».
    if (!loading && !error && workspaces.length === 0) return null;

    return (
      <Section data-testid="student-slack-deactivate" aria-live="polite">
        <SectionTitle>
          <SlackIcon size={13} strokeWidth={2} aria-hidden="true" />
          {t('pages.studentDetail.slack.modalTitle')}
        </SectionTitle>
        {error && workspaces.length === 0 && (
          <Hint role="status">{t('pages.studentDetail.slack.loadError')}</Hint>
        )}
        {loading && !error && workspaces.length === 0 && (
          <Hint>{t('pages.studentDetail.slack.loading')}</Hint>
        )}
        {workspaces.length > 0 && (
          <Hint>{t('pages.studentDetail.slack.modalIntro')}</Hint>
        )}
        {workspaces.map((workspace) => (
          <StudentSlackLinks
            key={workspace.groupId}
            id={`lms-block-slack-${workspace.groupId}`}
            workspace={workspace}
            variant="deactivate"
            showGroup
          />
        ))}
      </Section>
    );
  },
);

StudentSlackDeactivateHint.displayName = 'StudentSlackDeactivateHint';

const Box = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 6px 0;
  min-width: 0;
`;

const Head = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  font-size: 12px;
`;

const GroupName = styled.span`
  font-weight: 600;
  color: var(--k-fg-0f172a);
`;

const Muted = styled.span`
  color: var(--k-fg-64748b);
  overflow-wrap: anywhere;
`;

const Links = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
`;

/** Та же «ссылка-кнопка», что `ChipLink` карточек: синий акцент, 12px. */
const ExternalLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: var(--k-fg-2563eb);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid var(--k-border-2563eb);
    outline-offset: 1px;
  }

  &[data-tone='danger'] {
    color: ${tagToneColors('danger').fg};
  }
`;

const Hint = styled.span`
  display: block;
  font-size: 12px;
  line-height: 16px;
  color: var(--k-fg-64748b);
`;

const Section = styled.div`
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid var(--k-border-e2e8f0);
`;

const SectionTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--k-fg-475569);
  margin-bottom: 4px;
`;
